import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import { ThemeProvider } from '@/components/theme/theme-provider';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { AuthGate } from '@/features/auth';

/**
 * Every application-wide provider, in the order they have to nest.
 *
 * `ThemeProvider` is outermost because the `Toaster` reads the active theme.
 * `QueryClientProvider` sits above the router so the route error boundary can
 * clear the cache. `TooltipProvider` is hoisted once here rather than repeated
 * per component, which is also what lets tooltips share a hover delay instead of
 * each restarting the timer. `AuthGate` is innermost of the four and still above
 * the router: it needs the `QueryClient` to read the session, and it renders the
 * sign-in screen *instead of* the router when nobody is signed in (spec R7.5).
 * The `Toaster` sits outside the gate so a toast can fire on the sign-in screen
 * too.
 */
export const AppProviders = ({
  queryClient,
  children,
}: {
  queryClient: QueryClient;
  children: ReactNode;
}) => (
  <ThemeProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={200} skipDelayDuration={300}>
        <AuthGate>{children}</AuthGate>
        <Toaster position="bottom-right" closeButton />
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
);
